// ============================================================================
// PURCHASE INVOICE SERVICE (ADR-0049) — entity + BU scoped.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { purchaseInvoiceEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery, appendRepeatedQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, ID,
  PurchaseInvoiceDto, CreatePurchaseInvoiceRequest, UpdatePurchaseInvoiceRequest,
  PurchaseInvoiceQueryParams, SupplierDuplicateProbe, ReviseItcRequest, UpdatePurchaseInvoiceDetailsRequest,
  PurchaseTaxPreviewRequest, TaxCalculationResult, DocumentLineLedgerDto,
} from '../types';

class PurchaseInvoiceService {
  async getAll(params?: PurchaseInvoiceQueryParams): Promise<PaginatedResponse<PurchaseInvoiceDto>> {
    const endpoint = params ? buildEndpointWithQuery(purchaseInvoiceEndpoints.list(), params) : purchaseInvoiceEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<PurchaseInvoiceDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }
  async getById(id: ID): Promise<PurchaseInvoiceDto> {
    const response = await get<ApiResponse<PurchaseInvoiceDto>>(purchaseInvoiceEndpoints.getById(id));
    return response.data.data;
  }
  /** The §2 duplicate WARNING probe — never blocks; the screen words the result. */
  async duplicateCheck(partyId: ID, supplierInvoiceNumber: string, excludeId?: ID): Promise<SupplierDuplicateProbe[]> {
    const url = buildEndpointWithQuery(purchaseInvoiceEndpoints.duplicateCheck(), {
      partyId, supplierInvoiceNumber, excludeId: excludeId || undefined,
    });
    const response = await get<ApiResponse<SupplierDuplicateProbe[]>>(url);
    return response.data.data || [];
  }
  /**
   * The engine's tax for the bill's item lines (input side). `success: false` carries the reasons
   * (unmapped item, missing supplier state / registration) — the screen shows them, never blocks.
   */
  async taxPreview(data: PurchaseTaxPreviewRequest): Promise<TaxCalculationResult> {
    const response = await post<ApiResponse<TaxCalculationResult>>(purchaseInvoiceEndpoints.taxPreview(), data);
    return response.data.data;
  }
  /** The ledger each item posts to on the booking date (goods ledger, or the editable pre-fill). */
  async getLineLedgers(date: string, itemIds: string[]): Promise<DocumentLineLedgerDto[]> {
    const url = appendRepeatedQuery(buildEndpointWithQuery(purchaseInvoiceEndpoints.lineLedgers(), { date }), 'itemIds', itemIds);
    const response = await get<ApiResponse<DocumentLineLedgerDto[]>>(url);
    return response.data.data || [];
  }
  async create(data: CreatePurchaseInvoiceRequest): Promise<PurchaseInvoiceDto> {
    const response = await post<ApiResponse<PurchaseInvoiceDto>>(purchaseInvoiceEndpoints.create(), data);
    return response.data.data;
  }
  async update(id: ID, data: UpdatePurchaseInvoiceRequest): Promise<PurchaseInvoiceDto> {
    const response = await put<ApiResponse<PurchaseInvoiceDto>>(purchaseInvoiceEndpoints.update(id), data);
    return response.data.data;
  }
  /** Edit details — narration, due date, supplier number, descriptions; no re-post (§7, Slice 2c). */
  async updateDetails(id: ID, data: UpdatePurchaseInvoiceDetailsRequest): Promise<PurchaseInvoiceDto> {
    const response = await put<ApiResponse<PurchaseInvoiceDto>>(purchaseInvoiceEndpoints.details(id), data);
    return response.data.data;
  }
  /** Revise ITC eligibility on a posted bill (ADR-0049 §4b) — posts a reclassification journal on its own date. */
  async reviseItc(id: ID, data: ReviseItcRequest): Promise<PurchaseInvoiceDto> {
    const response = await post<ApiResponse<PurchaseInvoiceDto>>(purchaseInvoiceEndpoints.itcRevision(id), data);
    return response.data.data;
  }
  async remove(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(purchaseInvoiceEndpoints.remove(id), { rowVersion });
    await del<ApiResponse<unknown>>(url);
  }
  async cancel(id: ID, rowVersion: number): Promise<void> {
    const url = buildEndpointWithQuery(purchaseInvoiceEndpoints.cancel(id), { rowVersion });
    await post<ApiResponse<unknown>>(url, {});
  }
}

export const purchaseInvoiceService = new PurchaseInvoiceService();
export default purchaseInvoiceService;
